import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import supabase from '../../supabaseClient';

const AuditLogs = () => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState('all');
  const [filter, setFilter] = useState('all');
  const [expandedLogs, setExpandedLogs] = useState(new Set());
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedUser !== 'all') {
      fetchAuditLogs();
    }
  }, [selectedUser, filter]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('user_id, user_name, first_name, last_name, role, department')
        .order('user_name', { ascending: true });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users', { position: 'top-right' });
    }
  };

  const fetchAuditLogs = async (loadMore = false) => {
    if (selectedUser === 'all') {
      setAuditLogs([]);
      setPagination({ total: 0, limit: 50, offset: 0, hasMore: false });
      return;
    }

    try {
      setLoading(true);
      const offset = loadMore ? pagination.offset + pagination.limit : 0;

      const { data } = await axios.get(
        `${API_URL}/api/superadmin/user-audit-logs/${selectedUser}`,
        {
          params: {
            limit: pagination.limit,
            offset: offset,
            event_type: filter
          }
        }
      );

      if (data.success) {
        if (loadMore) {
          setAuditLogs(prev => [...prev, ...data.data]);
        } else {
          setAuditLogs(data.data);
        }
        setPagination(data.pagination);
      }
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs', { position: 'top-right' });
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (logId) => {
    const newExpanded = new Set(expandedLogs);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedLogs(newExpanded);
  };

  const getEventIcon = (eventType) => {
    const icons = {
      login: 'fa-sign-in-alt',
      logout: 'fa-sign-out-alt',
      created: 'fa-plus-circle',
      updated: 'fa-edit',
      deleted: 'fa-trash',
      viewed: 'fa-eye',
      downloaded: 'fa-download',
      exported: 'fa-file-export',
      password_changed: 'fa-key',
      approved: 'fa-check-circle',
      returned: 'fa-undo',
      cancelled: 'fa-times-circle'
    };
    return icons[eventType] || 'fa-circle';
  };

  const getEventColor = (eventType) => {
    const colors = {
      login: 'text-blue bg-blue/10 border-blue/30',
      logout: 'text-gray-600 bg-gray-100 border-gray-300',
      created: 'text-green-600 bg-green-100 border-green-300',
      updated: 'text-yellow-600 bg-yellow-100 border-yellow-300',
      deleted: 'text-red bg-red/10 border-red/30',
      viewed: 'text-gray-500 bg-gray-50 border-gray-200',
      downloaded: 'text-purple-600 bg-purple-100 border-purple-300',
      exported: 'text-indigo-600 bg-indigo-100 border-indigo-300',
      password_changed: 'text-orange-600 bg-orange-100 border-orange-300',
      approved: 'text-emerald-600 bg-emerald-100 border-emerald-300',
      returned: 'text-amber-600 bg-amber-100 border-amber-300',
      cancelled: 'text-red-600 bg-red-100 border-red-300'
    };
    return colors[eventType] || 'text-gray-600 bg-gray-100 border-gray-300';
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Convert technical field names to user-friendly labels
  const getFieldLabel = (field) => {
    const fieldLabels = {
      status: 'Status',
      currentstep: 'Current Step',
      remarks: 'Remarks',
      brokerage_remarks: 'Brokerage Remarks',
      shipper_name: 'Shipper Name',
      shipper_email: 'Shipper Email',
      shipper_phone: 'Shipper Phone',
      shipper_country: 'Shipper Country',
      shipper_city: 'Shipper City',
      shipper_street: 'Shipper Street',
      receiver_name: 'Receiver Name',
      receiver_email: 'Receiver Email',
      receiver_phone: 'Receiver Phone',
      receiver_country: 'Receiver Country',
      receiver_city: 'Receiver City',
      receiver_street: 'Receiver Street',
      shipment_description: 'Description',
      shipment_type: 'Shipment Type',
      shipment_mode: 'Shipment Mode',
      shipment_weight: 'Weight',
      shipment_grossweight: 'Gross Weight',
      shipment_chargeableweight: 'Chargeable Weight',
      shipment_dimensions: 'Dimensions',
      shipment_packages: 'Packages',
      shipment_volume: 'Volume',
      delivery_mode: 'Delivery Mode',
      document_urls: 'Documents',
      eta: 'Expected Arrival',
      etd: 'Expected Departure',
      is_billed: 'Billing Status',
      job_order: 'Job Order',
      shipment_transport: 'Transport Type',
      commodity: 'Commodity',
      number_pcs: 'Number of Pieces',
      cbm: 'CBM',
      origin: 'Origin',
      destination: 'Destination',
      shipment_bound: 'Shipment Bound',
      flight: 'Flight Number',
      truck_type: 'Truck Type',
      corporate_client: 'Corporate Client',
      shipment_code: 'Shipment Code',
      shipment_terms: 'Terms',
      shipment_containers: 'Containers'
    };
    return fieldLabels[field] || field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderChangedFields = (log) => {
    if (!log.changed_fields || log.changed_fields.length === 0) return null;

    return (
      <div className="mt-3 p-3 bg-white rounded-xl border border-gray-200">
        <div className="text-xs font-semibold text-grey mb-2">What Changed:</div>
        <div className="flex flex-wrap gap-2">
          {log.changed_fields.map((field, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-blue/10 text-blue border border-blue/20"
            >
              {getFieldLabel(field)}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) return 'Not set';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const renderBeforeAfter = (log) => {
    if (!log.before_state && !log.after_state) return null;

    // Get all unique field keys from both before and after states
    const allFields = new Set([
      ...Object.keys(log.before_state || {}),
      ...Object.keys(log.after_state || {})
    ]);

    if (allFields.size === 0) return null;

    return (
      <div className="mt-3 p-3 bg-white rounded-xl border border-gray-200">
        <div className="text-xs font-semibold text-grey mb-3">Changes:</div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-2 px-3 font-semibold text-grey">Field</th>
                <th className="text-left py-2 px-3 font-semibold text-red">Old Value</th>
                <th className="text-center py-2 px-1">
                  <i className="fas fa-arrow-right text-grey"></i>
                </th>
                <th className="text-left py-2 px-3 font-semibold text-green-700">New Value</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(allFields).map((field) => {
                const beforeVal = log.before_state?.[field];
                const afterVal = log.after_state?.[field];

                return (
                  <tr key={field} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium text-grey">{getFieldLabel(field)}</td>
                    <td className="py-2 px-3 text-grey bg-red/5">
                      <span className="font-mono text-xs">{formatValue(beforeVal)}</span>
                    </td>
                    <td className="text-center py-2 px-1">
                      <i className="fas fa-long-arrow-alt-right text-grey/40"></i>
                    </td>
                    <td className="py-2 px-3 text-grey bg-green-50">
                      <span className="font-mono text-xs">{formatValue(afterVal)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderMetadata = (log) => {
    if (!log.metadata || Object.keys(log.metadata).length === 0) return null;

    const getMetadataLabel = (key) => {
      const labels = {
        tracking_no: 'Tracking Number',
        shipment_type: 'Shipment Type',
        field_count: 'Fields Changed',
        update_type: 'Update Type'
      };
      return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const getMetadataValue = (key, value) => {
      if (key === 'update_type' && value === 'shipment_field_update') {
        return 'Shipment Update';
      }
      return formatValue(value);
    };

    return (
      <div className="mt-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
        <div className="text-xs font-semibold text-grey mb-2">Additional Information:</div>
        <div className="text-xs text-grey space-y-1">
          {Object.entries(log.metadata)
            .filter(([key]) => !['update_type'].includes(key)) // Hide technical fields
            .map(([key, value]) => (
              <div key={key} className="flex items-start gap-2">
                <i className="fas fa-info-circle text-blue mt-0.5"></i>
                <div>
                  <span className="font-medium">{getMetadataLabel(key)}:</span>{' '}
                  {getMetadataValue(key, value)}
                </div>
              </div>
            ))
          }
        </div>
      </div>
    );
  };

  const selectedUserData = users.find(u => u.user_id === parseInt(selectedUser));

  return (
    <div className="font-poppins min-h-screen bg-gray-100">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        {/* Header */}
        <div className="bg-blue px-6 py-6 rounded-t-2xl">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-2xl">
              <i className="fas fa-history text-white text-2xl"></i>
            </div>
            <div>
              <h1 className="text-white font-bold text-2xl">User Audit Logs</h1>
              <p className="text-white/80">Track and monitor all user activities across the system</p>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="px-6 py-5 border-b border-gray-200 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* User Selection */}
            <div>
              <label className="block text-sm font-semibold text-blue mb-2">
                <i className="fas fa-user mr-2"></i>
                Select User
              </label>
              <select
                value={selectedUser}
                onChange={(e) => {
                  setSelectedUser(e.target.value);
                  setExpandedLogs(new Set());
                }}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-blue font-medium focus:outline-none focus:ring-2 focus:ring-blue/30 transition-all"
              >
                <option value="all">-- Select a user to view logs --</option>
                {users.map((user) => (
                  <option key={user.user_id} value={user.user_id}>
                    {user.user_name} - {user.first_name} {user.last_name} ({user.role})
                  </option>
                ))}
              </select>
            </div>

            {/* Event Type Filter */}
            <div>
              <label className="block text-sm font-semibold text-blue mb-2">
                <i className="fas fa-filter mr-2"></i>
                Filter by Event Type
              </label>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    filter === 'all'
                      ? 'bg-blue text-white shadow-md'
                      : 'bg-white text-grey border border-gray-200 hover:border-blue/30'
                  }`}
                >
                  All
                </button>
                <button
                  onClick={() => setFilter('login')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    filter === 'login'
                      ? 'bg-blue text-white shadow-md'
                      : 'bg-white text-grey border border-gray-200 hover:border-blue/30'
                  }`}
                >
                  Login/Logout
                </button>
                <button
                  onClick={() => setFilter('created')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    filter === 'created'
                      ? 'bg-blue text-white shadow-md'
                      : 'bg-white text-grey border border-gray-200 hover:border-blue/30'
                  }`}
                >
                  Created
                </button>
                <button
                  onClick={() => setFilter('updated')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    filter === 'updated'
                      ? 'bg-blue text-white shadow-md'
                      : 'bg-white text-grey border border-gray-200 hover:border-blue/30'
                  }`}
                >
                  Updated
                </button>
                <button
                  onClick={() => setFilter('deleted')}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                    filter === 'deleted'
                      ? 'bg-blue text-white shadow-md'
                      : 'bg-white text-grey border border-gray-200 hover:border-blue/30'
                  }`}
                >
                  Deleted
                </button>
              </div>
            </div>
          </div>

          {/* Selected User Info */}
          {selectedUserData && (
            <div className="mt-4 p-4 bg-blue/5 rounded-xl border border-blue/20">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-blue text-white rounded-full flex items-center justify-center font-bold text-lg">
                  {selectedUserData.first_name?.[0]}{selectedUserData.last_name?.[0]}
                </div>
                <div>
                  <div className="font-semibold text-blue">
                    {selectedUserData.first_name} {selectedUserData.last_name}
                  </div>
                  <div className="text-sm text-grey">
                    @{selectedUserData.user_name} • {selectedUserData.role} • {selectedUserData.department}
                  </div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-2xl font-bold text-blue">{pagination.total}</div>
                  <div className="text-xs text-grey">Total Activities</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Timeline Content */}
        <div className="px-6 py-6 min-h-[400px]">
          {selectedUser === 'all' ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 bg-blue/10 rounded-full flex items-center justify-center mb-6">
                <i className="fas fa-users text-blue text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-blue mb-2">Select a User</h3>
              <p className="text-grey text-center max-w-md">
                Please select a user from the dropdown above to view their activity history and audit logs
              </p>
            </div>
          ) : loading && auditLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="relative">
                <div className="w-16 h-16 border-4 border-blue/20 border-t-blue rounded-full animate-spin"></div>
              </div>
              <div className="text-blue font-medium text-lg mt-4">Loading audit logs...</div>
            </div>
          ) : auditLogs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-20 h-20 bg-blue/10 rounded-full flex items-center justify-center mb-6">
                <i className="fas fa-history text-blue text-3xl"></i>
              </div>
              <h3 className="text-xl font-semibold text-blue mb-2">No Activity Found</h3>
              <p className="text-grey">No audit logs available for this user with the selected filter</p>
            </div>
          ) : (
            <div className="space-y-4">
              {auditLogs.map((log, index) => (
                <div
                  key={log.id}
                  className="relative pl-8 pb-4 border-l-2 border-gray-200 last:border-l-0 last:pb-0"
                >
                  {/* Timeline Dot */}
                  <div className={`absolute left-0 top-0 transform -translate-x-1/2 w-8 h-8 rounded-full flex items-center justify-center border-2 ${getEventColor(log.event_type)}`}>
                    <i className={`fas ${getEventIcon(log.event_type)} text-xs`}></i>
                  </div>

                  {/* Log Content */}
                  <div className="bg-white rounded-2xl p-5 shadow-md border border-gray-100 hover:shadow-lg transition-all duration-200 ml-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-semibold border ${getEventColor(log.event_type)}`}>
                            {log.event_type}
                          </span>
                          {log.entity_type && (
                            <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-grey border border-gray-200">
                              {log.entity_type}
                            </span>
                          )}
                          <span className="text-xs text-grey">
                            {formatDateTime(log.created_at)}
                          </span>
                        </div>
                        <p className="text-base font-medium text-blue mb-1">
                          {log.action_description}
                        </p>
                        {log.metadata?.location && (log.event_type === 'login' || log.event_type === 'logout') && (
                          <p className="text-sm text-grey flex items-center gap-1 mt-2">
                            <i className="fas fa-map-marker-alt text-blue"></i>
                            <span>
                              {log.metadata.location.city}, {log.metadata.location.country}
                            </span>
                          </p>
                        )}
                        {log.actor_name && log.actor_name !== selectedUserData?.user_name && (
                          <p className="text-sm text-grey mt-1">
                            by <span className="font-semibold">{log.actor_name}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <span className="text-sm text-grey whitespace-nowrap">
                          {formatTimeAgo(log.created_at)}
                        </span>
                        {(log.changed_fields?.length > 0 || log.before_state || log.after_state || (log.metadata && Object.keys(log.metadata).length > 0)) && (
                          <button
                            onClick={() => toggleExpanded(log.id)}
                            className="text-sm text-blue hover:text-blue/80 font-medium flex items-center gap-1"
                          >
                            {expandedLogs.has(log.id) ? (
                              <>Hide Details <i className="fas fa-chevron-up text-xs"></i></>
                            ) : (
                              <>Show Details <i className="fas fa-chevron-down text-xs"></i></>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    {expandedLogs.has(log.id) && (
                      <div className="mt-4 space-y-3">
                        {renderChangedFields(log)}
                        {renderBeforeAfter(log)}
                        {renderMetadata(log)}
                      </div>
                    )}
                  </div>
                </div>
              ))}

              {/* Load More Button */}
              {pagination.hasMore && (
                <div className="flex justify-center pt-4">
                  <button
                    onClick={() => fetchAuditLogs(true)}
                    disabled={loading}
                    className="px-6 py-3 bg-blue hover:bg-blue/90 text-white font-medium rounded-2xl transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-arrow-down mr-2"></i>
                        Load More ({pagination.total - auditLogs.length} remaining)
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {selectedUser !== 'all' && auditLogs.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <div className="text-sm text-grey">
              Showing <span className="font-semibold text-blue">{auditLogs.length}</span> of{' '}
              <span className="font-semibold text-blue">{pagination.total}</span> records
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogs;
